import React, { useCallback } from "react";
import { Share, StyleSheet } from "react-native";

import { formatPrice, isNative, isWeb, useGetMyReferralQuery } from "@stepapp/core";

import { addSentryBreadcrumb } from "@stepapp/sentry";
import {
  Block,
  BottomSheet,
  Button,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { useAppSelector, useContactDetails, useCopyToClipboard } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";

type Props = {
  toggle: () => void;
  visible: boolean;
};

const backgroundColor = "#F4D5D3";

const ShareCouponSheet = ({ toggle, visible }: Props) => {
  const { t } = useTranslation();
  const isLoggedIn = useAppSelector(({ user }) => !!user?.id);
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));
  const { AppUrl } = useContactDetails();

  const { data } = useGetMyReferralQuery({ skip: !isLoggedIn });

  const { code, providerReward, value } = data?.myReferral || {};

  const copyToClipboard = useCopyToClipboard(code);

  const shareMessage = useCallback(async () => {
    try {
      const result = await Share.share({
        message: t("components.shareCouponSheet.shareMessage", {
          referralCode: code,
          codeValue: priceFormatter(value),
          appUrl: AppUrl,
        }),
      });
      if (result.action === Share.sharedAction) {
      }
    } catch (error) {
      addSentryBreadcrumb("Error while sharing referral code", {
        data: JSON.stringify(error),
      });
    }
  }, [code, value, t, priceFormatter, AppUrl]);

  const handleShareReferralCode = useCallback(() => {
    copyToClipboard();
    if (isNative) {
      shareMessage();
    }
  }, [shareMessage, copyToClipboard]);

  return (
    <BottomSheet toggle={toggle} visible={visible}>
      <Touchable onPress={toggle} style={styles.closeButton}>
        <SvgIcon color={palette.font.primary} name="close" size={22} />
      </Touchable>

      <Block alignCenter justifyCenter>
        <Block alignCenter justifyCenter marginBottom={4} style={styles.couponContainer}>
          <Typography style={styles.coupon} variant="largeTitle">
            {code}
          </Typography>
        </Block>

        <Typography style={styles.accentText} variant="title">
          {t("components.shareCouponSheet.title")}
        </Typography>

        <Block paddingVertical={3}>
          {providerReward && (
            <Typography center style={styles.accentText} type="primary">
              {t("components.shareCouponSheet.description", {
                providerReward: priceFormatter(providerReward),
                receiverReward: priceFormatter(value),
              })}
            </Typography>
          )}
        </Block>

        <Block paddingVertical={2}>
          <Typography type="secondary" variant="label">
            {t("components.shareCouponSheet.discountExplanation")}
          </Typography>
        </Block>

        <Button
          label={
            isWeb ? t("components.shareCouponSheet.copy") : t("components.shareCouponSheet.share")
          }
          onPress={handleShareReferralCode}
          style={styles.shareButton}
          type="primary"
        />
      </Block>
    </BottomSheet>
  );
};

export default React.memo(ShareCouponSheet);

const styles = StyleSheet.create({
  accentText: {
    color: palette.primary.accent,
  },
  closeButton: {
    margin: spacing.unit * 2,
    width: spacing.unit * 4,
  },
  coupon: {
    color: palette.secondary.accent,
  },
  couponContainer: {
    backgroundColor,
    minHeight: spacing.unit * 7,
    minWidth: spacing.unit * 24,
  },
  shareButton: {
    marginBottom: spacing.unit,
    width: spacing.unit * 30,
  },
});
